import {
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { BiPlus, BiTrash } from 'react-icons/bi'
import {
  Divider,
  Flex,
  FormControl,
  IconButton,
  InputGroup,
  InputRightElement,
  Skeleton,
  Stack,
} from '@chakra-ui/react'
import validator from 'validator'

import { FormWebhook } from '~shared/types/form/form'

import { GUIDE_WEBHOOKS } from '~constants/links'
import Button from '~components/Button'
import FormErrorMessage from '~components/FormControl/FormErrorMessage'
import FormLabel from '~components/FormControl/FormLabel'
import Input from '~components/Input'
import Spinner from '~components/Spinner'
import Toggle from '~components/Toggle'

import { useMutateFormSettings } from '../../mutations'
import { useAdminFormSettings } from '../../queries'

type WebhookEntryProps = {
  webhook: FormWebhook
  index: number
  isOnlyEntry: boolean
  isSaving: boolean
  onUrlChange: (index: number, url: string) => void
  onRetryChange: (index: number, enabled: boolean) => void
  onRemove: (index: number) => void
}

const WebhookEntry = ({
  webhook,
  index,
  isOnlyEntry,
  isSaving,
  onUrlChange,
  onRetryChange,
  onRemove,
}: WebhookEntryProps): JSX.Element => {
  const [localUrl, setLocalUrl] = useState(webhook.url)
  const [urlError, setUrlError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setLocalUrl(webhook.url)
  }, [webhook.url])

  const validateUrl = (url: string): string => {
    if (!url) return ''
    if (
      !validator.isURL(url, {
        protocols: ['https'],
        require_protocol: true,
      })
    ) {
      return 'Please enter a valid URL (starting with https://)'
    }
    return ''
  }

  const handleBlur = useCallback(() => {
    const error = validateUrl(localUrl)
    if (error) {
      setUrlError(error)
      return
    }
    setUrlError('')
    onUrlChange(index, localUrl)
  }, [index, localUrl, onUrlChange])

  const handleKeyDown: KeyboardEventHandler = useCallback(
    (e) => {
      if (e.key === 'Enter' && !urlError) {
        inputRef.current?.blur()
      }
    },
    [urlError],
  )

  return (
    <Stack spacing="1.5rem">
      <FormControl isInvalid={!!urlError}>
        <Flex align="flex-end" gap="0.5rem">
          <Stack flex={1} spacing={0}>
            <FormLabel description="For developers and IT officers usage. We will POST encrypted form responses in real-time to the HTTPS endpoint specified here.">
              Endpoint URL
            </FormLabel>
            <InputGroup>
              {isSaving ? (
                <InputRightElement pointerEvents="none">
                  <Spinner />
                </InputRightElement>
              ) : null}
              <Input
                ref={inputRef}
                placeholder="https://your-webhook.com/url"
                value={localUrl}
                onChange={(e) => {
                  setLocalUrl(e.target.value)
                  setUrlError('')
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                isReadOnly={isSaving}
              />
            </InputGroup>
            <FormErrorMessage>{urlError}</FormErrorMessage>
          </Stack>
          {!isOnlyEntry && (
            <IconButton
              variant="clear"
              colorScheme="danger"
              icon={<BiTrash />}
              aria-label={`Remove webhook ${index + 1}`}
              mb={urlError ? '1.5rem' : 0}
              onClick={() => onRemove(index)}
            />
          )}
        </Flex>
      </FormControl>
      {webhook.url ? (
        <Toggle
          isLoading={isSaving}
          isChecked={webhook.isRetryEnabled}
          label="Enable retries"
          description={`Your system must meet certain requirements before retries can be safely enabled. [Learn more](${GUIDE_WEBHOOKS})`}
          onChange={(e) => onRetryChange(index, e.target.checked)}
        />
      ) : null}
    </Stack>
  )
}

export const WebhooksSection = (): JSX.Element => {
  const { data: settings, isLoading } = useAdminFormSettings()
  const { mutateFormWebhooks } = useMutateFormSettings()

  // Resolve webhooks: prefer new array, fall back to legacy single webhook
  const resolvedWebhooks: FormWebhook[] =
    settings?.webhooks && settings.webhooks.length > 0
      ? settings.webhooks
      : settings?.webhook?.url
      ? [
          {
            url: settings.webhook.url,
            isRetryEnabled: !!settings.webhook.isRetryEnabled,
          },
        ]
      : [{ url: '', isRetryEnabled: false }]

  const [webhooks, setWebhooks] = useState<FormWebhook[]>(resolvedWebhooks)

  useEffect(() => {
    if (!isLoading) {
      setWebhooks(resolvedWebhooks)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const saveWebhooks = useCallback(
    (nextWebhooks: FormWebhook[]) => {
      mutateFormWebhooks.mutate(nextWebhooks.filter((wh) => !!wh.url))
    },
    [mutateFormWebhooks],
  )

  const handleUrlChange = useCallback(
    (index: number, url: string) => {
      const next = webhooks.map((wh, i) => (i === index ? { ...wh, url } : wh))
      setWebhooks(next)
      saveWebhooks(next)
    },
    [saveWebhooks, webhooks],
  )

  const handleRetryChange = useCallback(
    (index: number, enabled: boolean) => {
      const next = webhooks.map((wh, i) =>
        i === index ? { ...wh, isRetryEnabled: enabled } : wh,
      )
      setWebhooks(next)
      saveWebhooks(next)
    },
    [saveWebhooks, webhooks],
  )

  const handleRemove = useCallback(
    (index: number) => {
      const next = webhooks.filter((_, i) => i !== index)
      const withDefault =
        next.length === 0 ? [{ url: '', isRetryEnabled: false }] : next
      setWebhooks(withDefault)
      saveWebhooks(withDefault)
    },
    [saveWebhooks, webhooks],
  )

  const handleAdd = useCallback(() => {
    setWebhooks((prev) => [...prev, { url: '', isRetryEnabled: false }])
  }, [])

  return (
    <Stack spacing="2.5rem">
      <Skeleton isLoaded={!isLoading}>
        <Stack spacing="2rem" divider={<Divider />}>
          {webhooks.map((webhook, index) => (
            <WebhookEntry
              key={index}
              webhook={webhook}
              index={index}
              isOnlyEntry={webhooks.length === 1}
              isSaving={mutateFormWebhooks.isLoading}
              onUrlChange={handleUrlChange}
              onRetryChange={handleRetryChange}
              onRemove={handleRemove}
            />
          ))}
        </Stack>
      </Skeleton>
      <Button
        variant="outline"
        leftIcon={<BiPlus />}
        alignSelf="flex-start"
        onClick={handleAdd}
        isDisabled={isLoading}
      >
        Add webhook
      </Button>
    </Stack>
  )
}
